/**
 * Reine Logik der Baum-Tabelle (Design-Guide `tabellen.md`, Archetyp B): sichtbare Zeilen aus dem Aufklapp-Zustand,
 * „Alle aufklappen“ (höchstens bis Tiefe 2 und höchstens 200 Zeilen) und die Tastatursteuerung (Pfeiltasten).
 * Kein React, keine Daten außer dem übergebenen Baum.
 */
import type { ReactNode } from "react";

export interface BaumKnoten {
  id: string;
  /** Erste Spalte: Name. */
  name: string;
  /** Zweite Zeile unter dem Namen (z. B. Personalnummer). */
  zweitzeile?: string;
  /** Haupt-Link der Zeile (genau ein Anker, erweiterte Klickfläche); ohne `href` bleibt der Name Text. */
  href?: string;
  /** Aktionsmenü der Zeile (z. B. `DropdownMenue`), nur mit Recht; kein Eintrag = nichts übergeben. */
  aktionen?: ReactNode;
  /** Zahl der Kinder, die erst beim Aufklappen nachgeladen werden (`kinder` ist dann leer). */
  ungeladen?: number;
  /** Weitere Spalten in der Reihenfolge der Spaltenköpfe (ohne die Namensspalte). */
  zellen: ReactNode[];
  kinder: BaumKnoten[];
}

export interface SichtbareZeile {
  knoten: BaumKnoten;
  /** 1 = Wurzel. */
  ebene: number;
  elternId: string | null;
  /** Namen der Vorfahren (ohne die Zeile selbst), für den Pfad ab Ebene 4. */
  pfad: string[];
  /** Position unter den Geschwistern (1-basiert) und Zahl der Geschwister. */
  position: number;
  geschwister: number;
  /** Zahl der Kinder (geladen oder angekündigt). */
  kinderAnzahl: number;
  /** Erstes geladenes Kind (Ziel von Pfeil rechts), sonst null. */
  ersteKindId: string | null;
}

/** Wirksame Kinder: nachgeladene Liste hat Vorrang vor `kinder`. */
export function kinderVon(k: BaumKnoten, nachgeladen?: ReadonlyMap<string, BaumKnoten[]>): BaumKnoten[] {
  return nachgeladen?.get(k.id) ?? k.kinder;
}

/** Kinderzahl für die Pille: geladene Kinder, sonst die angekündigte Zahl. */
export function kinderAnzahlVon(k: BaumKnoten, nachgeladen?: ReadonlyMap<string, BaumKnoten[]>): number {
  const nachgeladeneKinder = nachgeladen?.get(k.id);
  if (nachgeladeneKinder) return nachgeladeneKinder.length;
  return k.kinder.length > 0 ? k.kinder.length : (k.ungeladen ?? 0);
}

/** Höchste Zahl aufgeklappter Zeilen bei „Alle aufklappen“ (Guide: höchstens 200 Zeilen). */
export const MAX_ZEILEN_ALLE_AUF = 200;
/** „Alle aufklappen“ öffnet höchstens bis Tiefe 2 (Guide). */
export const MAX_TIEFE_ALLE_AUF = 2;

export function sichtbareZeilen(
  wurzeln: BaumKnoten[],
  offen: ReadonlySet<string>,
  nachgeladen?: ReadonlyMap<string, BaumKnoten[]>,
): SichtbareZeile[] {
  const out: SichtbareZeile[] = [];
  const gehe = (liste: BaumKnoten[], ebene: number, elternId: string | null, pfad: string[]) => {
    liste.forEach((k, i) => {
      const kinder = kinderVon(k, nachgeladen);
      out.push({
        knoten: k,
        ebene,
        elternId,
        pfad,
        position: i + 1,
        geschwister: liste.length,
        kinderAnzahl: kinderAnzahlVon(k, nachgeladen),
        ersteKindId: kinder[0]?.id ?? null,
      });
      if (kinder.length > 0 && offen.has(k.id)) gehe(kinder, ebene + 1, k.id, [...pfad, k.name]);
    });
  };
  gehe(wurzeln, 1, null, []);
  return out;
}

/** Menge der Ids für „Alle aufklappen“: Ebene 1 und 2 öffnen, solange insgesamt höchstens 200 Zeilen sichtbar sind. */
export function alleAufklappen(wurzeln: BaumKnoten[], nachgeladen?: ReadonlyMap<string, BaumKnoten[]>): Set<string> {
  const offen = new Set<string>();
  let sichtbar = wurzeln.length;
  const gehe = (liste: BaumKnoten[], ebene: number) => {
    for (const k of liste) {
      // Nur geladene Zweige: Ungeladenes wird nie „auf Verdacht“ nachgeladen.
      const kinder = kinderVon(k, nachgeladen);
      if (kinder.length === 0 || ebene > MAX_TIEFE_ALLE_AUF) continue;
      if (sichtbar + kinder.length > MAX_ZEILEN_ALLE_AUF) continue;
      offen.add(k.id);
      sichtbar += kinder.length;
      gehe(kinder, ebene + 1);
    }
  };
  gehe(wurzeln, 1);
  return offen;
}

// ---------- Ordnen (P-W2 Teil 2, additiv): Ziehen und Alt+Pfeile ----------

/** Wo eine gezogene Zeile auf der Zielzeile landet: davor, danach (Geschwister) oder als Kind. */
export type DropZone = "davor" | "kind" | "danach";

/** Breite der Randbänder in Pixeln (Drei-Zonen-Modell des Wiki: eine Baumzeile ist niedrig, deshalb feste Bänder statt Drittel). */
export const DROP_BAND_PX = 6;

/**
 * Zone aus der Mausposition innerhalb der Zielzeile: oberes Band = davor, unteres Band = danach, dazwischen = Kind. Ein
 * zugeklappter Knoten MIT Kindern hat kein unteres Band (sonst landet die Zeile unsichtbar hinter dem zugeklappten Zweig).
 */
export function dropZone(rect: { top: number; height: number }, clientY: number, hatZugeklappteKinder: boolean): DropZone {
  const y = clientY - rect.top;
  if (y <= DROP_BAND_PX) return "davor";
  if (!hatZugeklappteKinder && y >= rect.height - DROP_BAND_PX) return "danach";
  return "kind";
}

/** Ist `id` der Knoten `ahnId` selbst oder einer seiner Nachfahren (Ablegen darauf wäre ein Zyklus)? */
export function istNachfahre(wurzeln: readonly BaumKnoten[], ahnId: string, id: string): boolean {
  const finde = (liste: readonly BaumKnoten[]): BaumKnoten | null => {
    for (const k of liste) {
      if (k.id === ahnId) return k;
      const innen = finde(k.kinder);
      if (innen) return innen;
    }
    return null;
  };
  const ahn = finde(wurzeln);
  if (!ahn) return false;
  const enthaelt = (k: BaumKnoten): boolean => k.id === id || k.kinder.some(enthaelt);
  return enthaelt(ahn);
}

/** Alt + Pfeil: hoch/runter unter den Geschwistern, rechts = eine Ebene tiefer (unter den Vorgänger), links = eine Ebene höher. */
export type BaumVerschiebung =
  | { art: DropZone; id: string; zielId: string }
  | { art: "hoch" | "runter" | "tiefer" | "hoeher"; id: string };

export type BaumOrdnenErgebnis = { ok: true; ansage?: string } | { ok: false; meldung: string };

/** Schaltet das Ordnen der Baum-Tabelle ein (nur Personen mit dem Recht dazu; der Aufrufer prüft, die Aktion prüft selbst). */
export interface BaumOrdnen {
  /** Bedienhinweis über der Tabelle („Reihenfolge ändern: Griff ziehen oder Alt + Pfeiltasten.“). */
  hinweis?: string;
  /** Führt die Verschiebung aus (z. B. über die Bestandsaktion); der Satz der Ablehnung/des Fehlschlags kommt vom Aufrufer. */
  beiVerschieben: (v: BaumVerschiebung) => Promise<BaumOrdnenErgebnis>;
  /**
   * Zeilen, die der Schreibweg nicht verschiebt (z. B. wegen einer Sperre): kein Griff in der Zeile (die Griff-Zelle bleibt leer) und
   * keine Geste auf ihr (Anbieten = Erlauben, r110). Der Aufrufer füllt die Liste aus derselben Prüfung wie die Aktion.
   */
  nichtZiehbar?: readonly string[];
  /**
   * Fragt vor jeder Geste (Ziehen, Alt + Pfeil), ob der Schreibweg sie annehmen würde; `false` = Ziel wird nicht angeboten
   * (keine Ablage-Markierung), Alt + Pfeil tut nichts. Der Aufrufer wertet dieselbe Prüfung aus wie die Aktion (Anbieten = Erlauben).
   */
  erlaubt?: (v: BaumVerschiebung) => boolean;
}

export function tasteZuVerschiebung(taste: string, alt: boolean): "hoch" | "runter" | "tiefer" | "hoeher" | null {
  if (!alt) return null;
  switch (taste) {
    case "ArrowUp":
      return "hoch";
    case "ArrowDown":
      return "runter";
    case "ArrowRight":
      return "tiefer";
    case "ArrowLeft":
      return "hoeher";
    default:
      return null;
  }
}

export type BaumAktion =
  | { art: "fokus"; id: string }
  | { art: "oeffnen"; id: string }
  | { art: "schliessen"; id: string }
  | { art: "nichts" };

/** Pfeil auf/ab wechselt die Zeile, rechts öffnet bzw. springt ins erste Kind, links schließt bzw. springt zum Elternteil. */
export function tasteZuAktion(
  taste: string,
  aktuell: string,
  zeilen: SichtbareZeile[],
  offen: ReadonlySet<string>,
): BaumAktion {
  const i = zeilen.findIndex((z) => z.knoten.id === aktuell);
  if (i < 0) return { art: "nichts" };
  const z = zeilen[i]!;
  const hatKinder = z.kinderAnzahl > 0;
  switch (taste) {
    case "ArrowDown":
      return i < zeilen.length - 1 ? { art: "fokus", id: zeilen[i + 1]!.knoten.id } : { art: "nichts" };
    case "ArrowUp":
      return i > 0 ? { art: "fokus", id: zeilen[i - 1]!.knoten.id } : { art: "nichts" };
    case "Home":
      return { art: "fokus", id: zeilen[0]!.knoten.id };
    case "End":
      return { art: "fokus", id: zeilen[zeilen.length - 1]!.knoten.id };
    case "ArrowRight":
      if (!hatKinder) return { art: "nichts" };
      if (!offen.has(z.knoten.id)) return { art: "oeffnen", id: z.knoten.id };
      // Offen, aber (noch) ohne geladene Kinder (Nachladen läuft oder ist fehlgeschlagen): nicht springen.
      return z.ersteKindId ? { art: "fokus", id: z.ersteKindId } : { art: "nichts" };
    case "ArrowLeft":
      if (hatKinder && offen.has(z.knoten.id)) return { art: "schliessen", id: z.knoten.id };
      return z.elternId ? { art: "fokus", id: z.elternId } : { art: "nichts" };
    default:
      return { art: "nichts" };
  }
}
